import { action, computed, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, ReactNode, createContext, useContext, useMemo } from 'react'

export interface INavigationStackStore {
  goTo(step: number): void
  push(view: ReactNode): void
  pop(): void
  clear(): void
  replace(view: ReactNode): void
  currentView: ReactNode
  canGoBack: boolean
  currentStep: number
}

export class NavigationStackStore implements INavigationStackStore {
  viewStack: ReactNode[] = []
  constructor() {
    makeObservable(this, {
      viewStack: observable,
      currentView: computed,
      goTo: action,
      push: action,
      pop: action,
      clear: action,
      replace: action,
    })
  }

  goTo = (step: number) => {
    let targetIndex = step
    if (step < 0) {
      targetIndex = this.viewStack.length + step
    }
    if (targetIndex < 0 || targetIndex >= this.viewStack.length) {
      throw new Error('Invalid step index')
    }
    this.viewStack = this.viewStack.slice(0, targetIndex)
  }
  replace = (view: ReactNode) => {
    this.viewStack.pop()
    this.push(view)
  }

  push = (view: ReactNode) => {
    this.viewStack.push(view)
  }

  pop = () => {
    this.viewStack.pop()
  }

  clear = () => {
    this.viewStack = []
  }

  get currentView() {
    return this.viewStack[this.viewStack.length - 1]
  }

  get canGoBack() {
    return this.viewStack.length > 0
  }

  get currentStep() {
    return this.viewStack.length
  }
}

const NavigationContext = createContext<INavigationStackStore>(new NavigationStackStore())

export const useNavigationStore = () => useContext(NavigationContext)

export const NavigationStack: FC<{ children: ReactNode }> = observer(({ children }) => {
  const navigationStackStore = useMemo(() => new NavigationStackStore(), [])
  return (
    <NavigationContext.Provider value={navigationStackStore}>
      {navigationStackStore.currentView ?? children}
    </NavigationContext.Provider>
  )
})
